import React, {useContext, useEffect, useState} from 'react';
import {Map, List} from 'immutable';

import {Text, Icon} from '@albert-io/atomic';

import {StandardSetModelV1} from 'resources/augmented/StandardSet/StandardSetModel.v1';
import {StandardModelV1} from 'resources/augmented/Standard/StandardModel.v1';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import {useTracking} from 'react-tracking';
import sessionStore from 'client/Session/SessionStore';

import {
  getStandardSetQuestionCountQuery,
  getStandardQuestionCountsQuery
} from '../FilterGroup.queries';

import {ExpandableChip} from './ExpandableChip.react';

import './expandable-chip.scss';

interface StandardCounts {
  [standardId: string]: number;
}

interface StandardSetChipProps {
  standardSetObj: Map<string, any>;
  deselectStandardSet: (standardSetId: string) => void;
  deselectStandard: (standardSetId: string, standardId: string) => void;
}

const StandardSetChip = ({
  standardSetObj,
  deselectStandardSet,
  deselectStandard
}: StandardSetChipProps) => {
  const [standardCounts, setStandardCounts] = useState({} as StandardCounts);
  const [standardSetQuestionCount, setStandardSetQuestionCount] = useState();
  const [countsLoaded, setCountsLoaded] = useState(false);

  const {
    selectedGuideLevels,
    selectedSubjects,
    questionDifficulties,
    questionTypes,
    hideAssessmentQuestions,
    hidePreviouslyAssigned
  } = useContext(ContentDiscoveryContext);

  const standards = standardSetObj.get('standards').toArray();

  const {trackEvent} = useTracking({
    type: 'chip'
  });

  const updateStandardSetQuestionCounts = async (selectedStandardIds) => {
    const standardSetId = standardSetObj.get('standardSet').getId();

    const standardSetData: StandardSetModelV1 = await getStandardSetQuestionCountQuery(
      standardSetId,
      questionDifficulties,
      questionTypes,
      hideAssessmentQuestions,
      hidePreviouslyAssigned,
      selectedGuideLevels,
      '',
      selectedStandardIds
    ).getResourcePromise();

    const newStandardSetQuestionCount = standardSetData
      .getMeta()
      .getContentDiscoveryQuestionCount();

    setStandardSetQuestionCount(newStandardSetQuestionCount);
  };

  const updateStandardQuestionCounts = async (selectedStandardIds) => {
    const standardsData: List<StandardModelV1> = await getStandardQuestionCountsQuery(
      questionDifficulties,
      questionTypes,
      hideAssessmentQuestions,
      hidePreviouslyAssigned,
      selectedGuideLevels,
      '',
      selectedStandardIds
    ).getResourcePromise();

    const updatedCounts = {} as StandardCounts;

    standardsData.forEach((standardData) => {
      updatedCounts[standardData.getId()] =
        standardData.getMeta().getContentDiscoveryQuestionCount() || 0;
    });

    setStandardCounts(updatedCounts);
  };

  const updateCounts = async () => {
    setCountsLoaded(false);

    const selectedStandardIds: string[] = [];

    const standardSetIsIndeterminate = standardSetObj.get('isIndeterminate');

    const promises: Promise<void>[] = [];

    if (standardSetIsIndeterminate) {
      standards.forEach((standardObj: any) => {
        const id: string = standardObj.get('standard').getId();

        if (standardObj.get('isSelected')) {
          selectedStandardIds.push(id);
        }
      });

      promises.push(updateStandardQuestionCounts(selectedStandardIds));
    }

    promises.push(updateStandardSetQuestionCounts(selectedStandardIds));

    await Promise.all(promises);

    setCountsLoaded(true);
  };

  useEffect(() => {
    if (
      !sessionStore.isSuper ||
      (!standardSetObj.get('isSelected') && !standardSetObj.get('isIndeterminate'))
    ) {
      return;
    }

    updateCounts();
  }, [
    standardSetObj,
    questionDifficulties,
    questionTypes,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    selectedGuideLevels,
    selectedSubjects
  ]);

  return standardSetObj.get('isSelected') || standardSetObj.get('isIndeterminate') ? (
    <ExpandableChip
      className='u-mar-b_1'
      id={standardSetObj.get('standardSet').getId()}
      text={standardSetObj.get('standardSet').getTitle()}
      count={standardSetQuestionCount}
      showCount={countsLoaded}
      onExpandToggle={(on) => {
        trackEvent({
          event: 'click',
          type: on ? 'collapse' : 'expand',
          content: `standard set: ${standardSetObj.get('standardSet').getTitle()}`
        });
      }}
      onCancel={() => {
        deselectStandardSet(standardSetObj.get('standardSet').getId());
        trackEvent({
          event: 'click',
          content: `standard set: ${standardSetObj.get('standardSet').getTitle()}`
        });
      }}
    >
      {!standardSetObj.get('isSelected')
        ? standards.map((standardObj) => {
            if (standardObj.get('isSelected')) {
              const standardTitle = standardObj.get('standard').getTitle();
              const standardId = standardObj.get('standard').getId();
              return (
                <li className='expandable-chip__section-item u-pad_0 u-pad-l_1'>
                  <Text color='inherit' size='xs'>
                    {standardTitle}
                  </Text>
                  {
                    /*
                      TODO: Enable for all users once feature is complete
                      See https://github.com/albert-io/nexus/issues/7059
                    */
                    sessionStore.isSuper() && countsLoaded && (
                      <Text
                        className='expandable-chip_question-count u-flex_1'
                        color='inherit'
                        as='div'
                        size='xs'
                      >
                        {`(${standardCounts[standardId]})`}
                      </Text>
                    )
                  }
                  <button
                    className='expandable-chip__button'
                    type='button'
                    onClick={() => {
                      deselectStandard(
                        standardSetObj.get('standardSet').getId(),
                        standardObj.get('standard').getId()
                      );
                    }}
                    aria-label={`remove ${standardTitle}`}
                  >
                    <Icon icon='times' />
                  </button>
                </li>
              );
            }
            return null;
          })
        : null}
    </ExpandableChip>
  ) : null;
};

export const StandardSetChipGroup = () => {
  const {standardCollection, setStandardCollection} = useContext(ContentDiscoveryContext);

  const deselectStandardSet = (standardSetId) => {
    let newStandardCollection = standardCollection;
    newStandardCollection = newStandardCollection.setIn([standardSetId, 'isSelected'], false);
    newStandardCollection = newStandardCollection.setIn([standardSetId, 'isIndeterminate'], false);
    newStandardCollection = newStandardCollection.updateIn(
      [standardSetId, 'standards'],
      (standardsMap) => standardsMap.map((standard) => standard.set('isSelected', false))
    );
    setStandardCollection(newStandardCollection);
  };

  const deselectStandard = (standardSetId, standardId) => {
    let newStandardCollection = standardCollection.setIn(
      [standardSetId, 'standards', standardId, 'isSelected'],
      false
    );
    if (
      !newStandardCollection
        .getIn([standardSetId, 'standards'])
        .some((standard) => standard.get('isSelected'))
    ) {
      newStandardCollection = newStandardCollection.setIn(
        [standardSetId, 'isIndeterminate'],
        false
      );
    } else if (newStandardCollection.getIn([standardSetId, 'isSelected'])) {
      newStandardCollection = newStandardCollection.setIn([standardSetId, 'isSelected'], false);
    }

    setStandardCollection(newStandardCollection);
  };

  const hasSelectedStandards = standardCollection.toArray().some((standardSetObj) => {
    return standardSetObj.get('isSelected') || standardSetObj.get('isIndeterminate');
  });

  return hasSelectedStandards ? (
    <div className='u-mar-t_1'>
      {standardCollection.toArray().map((standardSetObj) => (
        <StandardSetChip
          deselectStandard={deselectStandard}
          deselectStandardSet={deselectStandardSet}
          standardSetObj={Map(standardSetObj)}
        />
      ))}
    </div>
  ) : null;
};
